/**
 * compressImage — resize + JPEG-compress a File using an offscreen Canvas.
 *
 * Targets ≤ maxDim on the longest side and JPEG quality 0.78.
 * A 12MP iPhone photo (8–15 MB) typically compresses to 200–450 KB,
 * making uploads ~20–40x faster without noticeable quality loss.
 *
 * @param {File} file        Original image File object.
 * @param {number} maxDim    Max width/height in pixels (default 1400).
 * @param {number} quality   JPEG quality 0–1 (default 0.78).
 * @returns {Promise<File>}  Compressed File with ".jpg" extension.
 */
export const compressImage = (file, maxDim = 1400, quality = 0.78) =>
  new Promise((resolve) => {
    // If already tiny, skip canvas round-trip.
    if (file.size < 300 * 1024) {
      resolve(file);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.round(img.naturalWidth * scale);
      const h = Math.round(img.naturalHeight * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          const name = file.name.replace(/\.[^.]+$/, ".jpg");
          resolve(new File([blob], name, { type: "image/jpeg", lastModified: Date.now() }));
        },
        "image/jpeg",
        quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); }; // fallback: use original
    img.src = objectUrl;
  });

/**
 * compressFiles — compress an array/FileList of images in parallel.
 * Returns an array of compressed File objects.
 */
export const compressFiles = (files, maxDim = 1400, quality = 0.78) =>
  Promise.all(Array.from(files).map((f) => compressImage(f, maxDim, quality)));
